export interface LatLng {
	lat: number;
	lng: number;
}

export interface GeoPoint extends LatLng {
	/** Précision GPS en mètres, quand elle est connue. */
	accuracy?: number | null;
	/** Cap en degrés (0 = nord), utile pour orienter le marqueur du taxi. */
	heading?: number | null;
	/** Vitesse en m/s. */
	speed?: number | null;
	/** Horodatage ISO de la mesure. */
	at?: string | null;
}

/** Centre approximatif de la Principauté (Monte-Carlo). */
export const MONACO_CENTER: LatLng = { lat: 43.7384, lng: 7.4246 };

/**
 * Boîte englobante de la Principauté, élargie d'une marge de service.
 *
 * La marge couvre les points de service courants hors territoire (aéroport de
 * Nice, Cap-d'Ail, Beausoleil, Roquebrune) : on refuse tout ce qui sort de cette
 * zone pour rester une application dédiée à Monaco.
 */
export const MONACO_BOUNDS = {
	south: 43.715,
	west: 7.38,
	north: 43.765,
	east: 7.45,
};

/** Zone de service élargie (Monaco + destinations autorisées : Nice aéroport, etc.). */
export const SERVICE_BOUNDS = {
	south: 43.6,
	west: 7.15,
	north: 43.85,
	east: 7.6,
};

/** Rayon par défaut de recherche de chauffeurs, en mètres. */
export const DEFAULT_DISPATCH_RADIUS_M = 3000;

/** Nombre de chauffeurs sollicités simultanément par vague de dispatch. */
export const DEFAULT_DISPATCH_FANOUT = 4;

/** Délai laissé à un chauffeur pour accepter une proposition, en secondes. */
export const DEFAULT_OFFER_TTL_S = 25;

const EARTH_RADIUS_M = 6_371_000;

const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Distance orthodromique entre deux points, en mètres. */
export function distanceMeters(a: LatLng, b: LatLng): number {
	const dLat = toRad(b.lat - a.lat);
	const dLng = toRad(b.lng - a.lng);
	const lat1 = toRad(a.lat);
	const lat2 = toRad(b.lat);

	const h =
		Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

	return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Cap en degrés de `a` vers `b` (0 = nord, sens horaire). */
export function bearingDegrees(a: LatLng, b: LatLng): number {
	const lat1 = toRad(a.lat);
	const lat2 = toRad(b.lat);
	const dLng = toRad(b.lng - a.lng);

	const y = Math.sin(dLng) * Math.cos(lat2);
	const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

	return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

export function isInBounds(point: LatLng, bounds = MONACO_BOUNDS): boolean {
	return (
		point.lat >= bounds.south &&
		point.lat <= bounds.north &&
		point.lng >= bounds.west &&
		point.lng <= bounds.east
	);
}

export const isInMonaco = (point: LatLng): boolean => isInBounds(point, MONACO_BOUNDS);
export const isInServiceArea = (point: LatLng): boolean => isInBounds(point, SERVICE_BOUNDS);

/**
 * Estimation grossière de la durée d'un trajet, en secondes.
 *
 * Monaco est dense, pentue et à sens uniques : la distance à vol d'oiseau est
 * multipliée par un facteur de sinuosité avant d'appliquer une vitesse moyenne.
 * Utilisé pour l'ETA affiché tant qu'aucun moteur de routage n'est branché.
 */
export function estimateDurationSeconds(from: LatLng, to: LatLng): number {
	const crowFlies = distanceMeters(from, to);
	const roadDistance = crowFlies * 1.45;
	const averageSpeedMs = 22 / 3.6; // ~22 km/h en agglomération monégasque
	return Math.max(60, Math.round(roadDistance / averageSpeedMs));
}

/** Formate une distance en mètres pour l'affichage (« 450 m », « 2,3 km »). */
export function formatDistance(meters: number, locale = 'fr'): string {
	if (meters < 1000) {
		return `${Math.round(meters / 10) * 10} m`;
	}
	return `${(meters / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })} km`;
}

/** Formate une durée en secondes (« 3 min », « 1 h 05 »). */
export function formatDuration(seconds: number): string {
	const minutes = Math.max(1, Math.round(seconds / 60));
	if (minutes < 60) {
		return `${minutes} min`;
	}
	const hours = Math.floor(minutes / 60);
	return `${hours} h ${String(minutes % 60).padStart(2, '0')}`;
}
